// src/components/TransactionsTable.tsx
import React, { useState, useRef, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import type { GridApi, GridReadyEvent } from 'ag-grid-community';
import { useTransactions } from './hooks/useTransactions';
import { combinedColDefs } from './aggrid/columnDefs';
import { useAllAttributions } from 'src/hooks/attributions/useAllAttributions';
import { useAddressAndChainParams } from 'src/hooks/routing/useAddressAndChainParams';
const PAGE_SIZE = 25;

export const TransactionsTable = () => {
  const [page, setPage] = useState(1);
  const gridApiRef = useRef<GridApi | null>(null);
  const { chain, address } = useAddressAndChainParams();

  const { data, isError, error, isLoading } = useTransactions({
    chain: chain,
    address: address,
    page,
    limit: PAGE_SIZE,
  });

  const rowData = data?.transactions ?? [];
  useAllAttributions(
    chain,
    rowData,
    address && rowData.length > 0 ? [address] : [],
  );
  const { hasNextPage, hasPrevPage } = data?.pagination ?? {};

  const onGridReady = useCallback((e: GridReadyEvent) => {
    gridApiRef.current = e.api;
  }, []);

  if (!chain || !address)
    return <div className="p-4 text-red-600">Missing chain or address</div>;
  if (isError)
    return (
      <div className="p-4 text-red-600">Error: {(error as Error).message}</div>
    );

  return (
    <div className="ag-theme-alpine w-full">
      <AgGridReact
        rowData={rowData}
        columnDefs={combinedColDefs}
        defaultColDef={{ sortable: true, resizable: false }}
        onGridReady={onGridReady}
        overlayLoadingTemplate={`<span class="ag-overlay-loading-center">Loading…</span>`}
        overlayNoRowsTemplate={`<span class="ag-overlay-no-rows-center">No transactions</span>`}
        suppressCellFocus
        domLayout="autoHeight"
      />

      {/* custom footer */}
      <div className="mt-4 flex justify-end text-sm text-gray-700">
        <div className="flex items-center space-x-2">
          <button
            disabled={page === 1 || isLoading}
            onClick={() => setPage(1)}
            className="rounded border px-2 py-1 disabled:opacity-50"
          >
            First
          </button>
          <button
            disabled={!hasPrevPage || isLoading}
            onClick={() => setPage((p) => p - 1)}
            className="rounded border px-2 py-1 disabled:opacity-50"
          >
            ‹
          </button>
          <span>
            Page {page}
            {isLoading ? ' …' : ''}
          </span>
          <button
            disabled={!hasNextPage || isLoading}
            onClick={() => setPage((p) => p + 1)}
            className="rounded border px-2 py-1 disabled:opacity-50"
          >
            ›
          </button>
        </div>
      </div>
    </div>
  );
};
